import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import type { PageRequest, SortDirection } from "@/types/api";
import {
  applyPageSortToSearchParams,
  type PageSort,
  pageSortEquivalent,
  parsePageSortFromSearchParams,
} from "@/lib/filterUrlParams";

/** Sort every list falls back to when neither the URL nor the caller says otherwise. */
const DEFAULT_LIST_SORT: PageSort = { sort: "atlasModifiedAt", direction: "desc" };

/** The sort a page request resolves to once `DEFAULT_LIST_SORT` fills any gaps. */
export function sortDefaultsFromPage(page: PageRequest | undefined): PageSort {
  return {
    sort: page?.sort ?? DEFAULT_LIST_SORT.sort,
    direction: page?.direction ?? DEFAULT_LIST_SORT.direction,
  };
}

/** `initialPage` with `?sort=…&sort_type=…` layered over its sort, for the first fetch. */
export function initialPageWithUrlSort(params: URLSearchParams, page: PageRequest | undefined): PageRequest {
  return { ...page, ...parsePageSortFromSearchParams(params, sortDefaultsFromPage(page)) };
}

/**
 * Keeps a list's sort (`pageRequest.sort` / `direction`) and `?sort=…&sort_type=ASC|DESC`
 * in lockstep. Writes use `replace`; reads only when the query string itself changes.
 */
export function useSortUrlSync(
  sort: string | undefined,
  direction: SortDirection | undefined,
  setSort: (field: string, direction: SortDirection) => void,
  defaults: PageSort,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const currentSort = sort ?? defaults.sort;
  const currentDirection = direction ?? defaults.direction;

  // State → URL.
  useEffect(() => {
    const current = { sort: currentSort, direction: currentDirection };
    const fromUrl = parsePageSortFromSearchParams(searchParamsRef.current, defaultsRef.current);
    if (pageSortEquivalent(fromUrl, current)) return;
    const next = applyPageSortToSearchParams(current, searchParamsRef.current, defaultsRef.current);
    if (next.toString() === searchParamsRef.current.toString()) return;
    setSearchParams(next, { replace: true });
  }, [currentSort, currentDirection, setSearchParams]);

  // URL → state (back/forward, pasted link).
  const searchParamsKey = searchParams.toString();
  const prevSearchParamsKey = useRef(searchParamsKey);
  useEffect(() => {
    if (prevSearchParamsKey.current === searchParamsKey) return;
    prevSearchParamsKey.current = searchParamsKey;
    const fromUrl = parsePageSortFromSearchParams(searchParams, defaultsRef.current);
    if (pageSortEquivalent(fromUrl, { sort: currentSort, direction: currentDirection })) return;
    setSort(fromUrl.sort, fromUrl.direction);
  }, [searchParamsKey, searchParams, currentSort, currentDirection, setSort]);
}

/** Component form, for hosts that render conditionally around other sync effects. */
export function SortUrlSyncEffect({
  sort,
  direction,
  setSort,
  defaults,
}: {
  sort: string | undefined;
  direction: SortDirection | undefined;
  setSort: (field: string, direction: SortDirection) => void;
  defaults: PageSort;
}) {
  useSortUrlSync(sort, direction, setSort, defaults);
  return null;
}
